import { useState, useCallback, useRef, useEffect } from 'react';

interface PopoverState {
  id: string;
  isOpen: boolean;
  priority: number;
}

export const usePopoverStateManager = () => {
  const [activePopovers, setActivePopovers] = useState<PopoverState[]>([]);
  const nextIdRef = useRef(0);

  // Register a new popover and get its ID
  const registerPopover = useCallback((priority: number = 0): string => {
    const id = `popover_${nextIdRef.current++}`;
    console.log(`[PopoverStateManager] Registering popover: ${id} with priority: ${priority}`);
    
    setActivePopovers(prev => [
      ...prev,
      { id, isOpen: false, priority }
    ]);
    
    return id;
  }, []);

  // Unregister a popover
  const unregisterPopover = useCallback((id: string) => {
    console.log(`[PopoverStateManager] Unregistering popover: ${id}`);
    setActivePopovers(prev => prev.filter(p => p.id !== id));
  }, []);

  // Open a popover (closes others if needed)
  const openPopover = useCallback((id: string) => {
    console.log(`[PopoverStateManager] Opening popover: ${id}`);
    
    setActivePopovers(prev => {
      const currentPopover = prev.find(p => p.id === id);
      if (!currentPopover) return prev;

      // Close other popovers with lower priority
      return prev.map(p => ({
        ...p,
        isOpen: p.id === id ? true : (p.priority >= currentPopover.priority ? p.isOpen : false)
      }));
    });
  }, []);

  // Close a popover
  const closePopover = useCallback((id: string) => {
    console.log(`[PopoverStateManager] Closing popover: ${id}`);
    setActivePopovers(prev => 
      prev.map(p => p.id === id ? { ...p, isOpen: false } : p)
    );
  }, []);

  // Close all popovers
  const closeAllPopovers = useCallback(() => {
    console.log('[PopoverStateManager] Closing all popovers');
    setActivePopovers(prev => prev.map(p => ({ ...p, isOpen: false })));
  }, []);

  // Get popover state
  const getPopoverState = useCallback((id: string): boolean => {
    const popover = activePopovers.find(p => p.id === id);
    return popover?.isOpen || false;
  }, [activePopovers]);

  // Check if any popover is open
  const hasOpenPopovers = activePopovers.some(p => p.isOpen);

  // Get highest priority open popover
  const getHighestPriorityPopover = useCallback(() => {
    const openPopovers = activePopovers.filter(p => p.isOpen);
    if (openPopovers.length === 0) return null;
    
    return openPopovers.reduce((highest, current) => 
      current.priority > highest.priority ? current : highest
    );
  }, [activePopovers]);

  // Prevent multiple popovers from conflicting
  const shouldPreventOpen = useCallback((id: string): boolean => {
    const popover = activePopovers.find(p => p.id === id);
    if (!popover) return false;

    const highestPriority = getHighestPriorityPopover();
    if (!highestPriority) return false;

    return highestPriority.priority > popover.priority;
  }, [activePopovers, getHighestPriorityPopover]);

  return {
    registerPopover,
    unregisterPopover,
    openPopover,
    closePopover,
    closeAllPopovers,
    getPopoverState,
    hasOpenPopovers,
    shouldPreventOpen,
    activePopovers: activePopovers.filter(p => p.isOpen)
  };
};

// Global context for popover state management
import { createContext, useContext } from 'react';

interface PopoverStateContextType {
  registerPopover: (priority?: number) => string;
  unregisterPopover: (id: string) => void;
  openPopover: (id: string) => void;
  closePopover: (id: string) => void;
  closeAllPopovers: () => void;
  getPopoverState: (id: string) => boolean;
  hasOpenPopovers: boolean;
  shouldPreventOpen: (id: string) => boolean;
}

export const PopoverStateContext = createContext<PopoverStateContextType | null>(null);

export const usePopoverState = () => {
  const context = useContext(PopoverStateContext);
  if (!context) {
    throw new Error('usePopoverState must be used within a PopoverStateProvider');
  }
  return context;
};